/**
 * Axiom Site — global helpers shared by component controllers.
 */
(function () {
  'use strict';

  if (window.Axiom) return;

  var root = document.documentElement;
  var lockCount = 0;
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  var FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), summary';

  function trapFocus(container, event) {
    if (event.key !== 'Tab') return;
    var nodes = Array.prototype.filter.call(container.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetParent !== null;
    });
    if (!nodes.length) return;
    var first = nodes[0];
    var last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  window.Axiom = {
    prefersReducedMotion: function () {
      return reducedMotion.matches;
    },
    lockScroll: function () {
      lockCount += 1;
      root.classList.add('is-locked');
    },
    unlockScroll: function () {
      lockCount = Math.max(0, lockCount - 1);
      if (!lockCount) root.classList.remove('is-locked');
    },
    trapFocus: trapFocus,
    focusable: FOCUSABLE
  };

  /* Accordions: animate native <details data-accordion> open and close. */
  function animateDetails(details, open) {
    var content = details.querySelector('.accordion__content');
    if (!content || reducedMotion.matches) {
      details.open = open;
      return;
    }
    if (details.dataset.animating === 'true') return;
    details.dataset.animating = 'true';
    if (open) details.open = true;
    var start = open ? 0 : content.scrollHeight;
    var end = open ? content.scrollHeight : 0;
    var animation = content.animate(
      [{ height: start + 'px', opacity: open ? 0 : 1 }, { height: end + 'px', opacity: open ? 1 : 0 }],
      { duration: 480, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
    );
    animation.onfinish = function () {
      if (!open) details.open = false;
      delete details.dataset.animating;
    };
  }

  /* <axiom-rotator>: cycles through slides (announcement bar messages). */
  if (!customElements.get('axiom-rotator')) {
    customElements.define(
      'axiom-rotator',
      class extends HTMLElement {
        connectedCallback() {
          this.slides = Array.from(this.querySelectorAll('[data-slide]'));
          this.index = 0;
          if (this.slides.length < 2) return;
          this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(-1, true));
          this.querySelector('[data-next]')?.addEventListener('click', () => this.go(1, true));
          this.addEventListener('pointerenter', () => this.stop());
          this.addEventListener('pointerleave', () => this.play());
          this.addEventListener('focusin', () => this.stop());
          this.play();
        }

        disconnectedCallback() {
          this.stop();
        }

        play() {
          this.stop();
          if (reducedMotion.matches) return;
          this.timer = setInterval(() => this.go(1, false), (parseFloat(this.dataset.interval) || 5) * 1000);
        }

        stop() {
          clearInterval(this.timer);
        }

        go(step, user) {
          const current = this.slides[this.index];
          this.index = (this.index + step + this.slides.length) % this.slides.length;
          const next = this.slides[this.index];
          current.classList.remove('is-active');
          current.setAttribute('aria-hidden', 'true');
          next.classList.add('is-active');
          next.removeAttribute('aria-hidden');
          this.dataset.direction = step > 0 ? 'next' : 'prev';
          if (user) this.play();
        }
      }
    );
  }

  /* <axiom-language>: dropdown on top of Shopify's localization form. */
  if (!customElements.get('axiom-language')) {
    customElements.define(
      'axiom-language',
      class extends HTMLElement {
        connectedCallback() {
          this.button = this.querySelector('[data-lang-toggle]');
          this.list = this.querySelector('[data-lang-list]');
          this.input = this.querySelector('[data-lang-input]');
          if (!this.button || !this.list) return;
          this.controller = new AbortController();
          var signal = this.controller.signal;

          this.button.addEventListener('click', () => this.toggle(this.list.hidden), { signal });
          this.list.addEventListener('click', (event) => {
            var option = event.target.closest('[data-value]');
            if (!option) return;
            event.preventDefault();
            if (option.dataset.value === this.input.value) {
              this.toggle(false);
              return;
            }
            this.input.value = option.dataset.value;
            this.classList.add('is-loading');
            this.input.form.submit();
          }, { signal });
          this.addEventListener('keydown', (event) => {
            if (this.list.hidden) return;
            var options = Array.from(this.list.querySelectorAll('[data-value]'));
            var index = options.indexOf(document.activeElement);
            if (event.key === 'Escape') {
              this.toggle(false);
              this.button.focus();
            } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              var step = event.key === 'ArrowDown' ? 1 : -1;
              options[(index + step + options.length) % options.length].focus();
            }
          }, { signal });
          document.addEventListener('click', (event) => {
            if (!this.contains(event.target)) this.toggle(false);
          }, { signal });
        }

        disconnectedCallback() {
          this.controller?.abort();
        }

        toggle(open) {
          this.list.hidden = !open;
          this.button.setAttribute('aria-expanded', String(open));
          this.classList.toggle('is-open', open);
          if (open) (this.list.querySelector('[aria-current]') || this.list.querySelector('[data-value]'))?.focus({ preventScroll: true });
        }
      }
    );
  }

  document.addEventListener('click', function (event) {
    var summary = event.target.closest && event.target.closest('details[data-accordion] > summary');
    if (!summary) return;
    event.preventDefault();
    var details = summary.parentElement;
    var willOpen = !details.open;
    if (willOpen && details.dataset.accordionGroup) {
      document
        .querySelectorAll('details[data-accordion][open][data-accordion-group="' + details.dataset.accordionGroup + '"]')
        .forEach(function (other) {
          if (other !== details) animateDetails(other, false);
        });
    }
    animateDetails(details, willOpen);
  });
})();
